import { useParams } from "react-router";
import { ArrowLeft, ExternalLink, Search } from "lucide-react";
import { AdminDataNotice } from "@/components/admin/data-notice";
import { Link } from "@shared/client/navigation";
import { usePageTitle, useView } from "@shared/client/view";
import { AdminChip, AdminMeta, AdminPanel, StatCard, StatGroup } from "@shared/components/blocks";
import { Alert, AlertDescription, AlertTitle } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import type { getAgentTrace } from "@shared/lib/admin-db";
import { formatDateTime, formatNumber } from "@shared/lib/format";
import { AGENT_TRACE_KIND_LABELS, AGENT_TRACE_PROVIDER_LABELS } from "./agent-labels";

type AgentTrace = NonNullable<Awaited<ReturnType<typeof getAgentTrace>>>;

/** Rend un JSON lisible, borné pour ne pas faire exploser la page. */
function prettyJson(value: unknown, maxChars = 400_000) {
  try {
    const pretty = JSON.stringify(value, null, 2) ?? "null";
    if (pretty.length <= maxChars) return pretty;
    return `${pretty.slice(0, maxChars)}\n… (tronqué à l'affichage — ${formatNumber(pretty.length)} caractères au total)`;
  } catch {
    return String(value);
  }
}

type SearchStep = { type?: string; query?: string; status?: string; url?: string; title?: string; date?: string };

/** Messages de type chat (boucles lib/llm.ts, anciennes traces Perplexity) ou instructions/input (anciennes traces OpenAI). */
function extractPrompts(request: Record<string, unknown>) {
  const prompts: Array<{ label: string; text: string }> = [];
  if (Array.isArray(request.messages)) {
    for (const message of request.messages) {
      if (message === null || typeof message !== "object") continue;
      const { role, content } = message as Record<string, unknown>;
      if (typeof content === "string") prompts.push({ label: String(role ?? "message"), text: content });
    }
  }
  if (typeof request.instructions === "string") prompts.push({ label: "instructions (system)", text: request.instructions });
  if (typeof request.input === "string") prompts.push({ label: "input (user)", text: request.input });
  return prompts;
}

export default function AdminAgentTracePage() {
  usePageTitle("Trace d'agent — Administration Hune");
  const { id = "" } = useParams();
  const { data, error } = useView<{ trace: AgentTrace }>(`/api/views/admin/agents/${encodeURIComponent(id)}`);
  if (error) return <AdminDataNotice error={error} />;
  if (!data) return null;
  const { trace } = data;

  const searches = (Array.isArray(trace.searches) ? trace.searches : []) as SearchStep[];
  const queries = searches.filter((step) => step.type === "search" && step.query);
  const pages = searches.filter((step) => step.type === "page" && step.url);
  const prompts = extractPrompts(trace.request ?? {});

  const preClass =
    "m-0 max-h-[420px] overflow-auto rounded-lg bg-muted/60 p-4 font-mono text-[12px] leading-relaxed whitespace-pre-wrap";

  return (
    <>
      <AdminMeta
        note={
          <>
            {trace.status === "error" ? "Appel en erreur" : "Appel réussi"}
            {trace.model && <> · modèle <code className="font-mono">{trace.model}</code></>}
          </>
        }
      >
        <span className="flex flex-wrap items-center gap-2">
          <Button asChild className="h-auto px-0 text-muted-foreground" size="sm" variant="link">
            <Link href="/admin/agents"><ArrowLeft className="size-3.5" /> Traces des agents</Link>
          </Button>
          ·
          <AdminChip tone="role">{AGENT_TRACE_PROVIDER_LABELS[trace.provider]}</AdminChip>
          {AGENT_TRACE_KIND_LABELS[trace.kind]}
          · {formatDateTime(trace.created_at)}
        </span>
      </AdminMeta>

      <StatGroup className="mb-4.5" columns={5}>
        <StatCard label="Durée" value={trace.duration_ms === null ? "—" : `${formatNumber(trace.duration_ms)} ms`} />
        <StatCard label="Étapes modèle" value={trace.turns === null ? "—" : formatNumber(trace.turns)} />
        <StatCard label="Tokens entrée" value={formatNumber(trace.input_tokens)} />
        <StatCard label="Tokens sortie" value={formatNumber(trace.output_tokens)} />
        <StatCard label="Tokens total" tint="teal" value={formatNumber(trace.total_tokens)} />
      </StatGroup>

      {(trace.organization || trace.run) && (
        <AdminPanel className="mb-4.5" title="Contexte">
          <ul className="m-0 grid list-none gap-2 p-0 text-[12.5px]">
            {trace.run && (
              <li>
                Analyse : <code className="font-mono">{trace.run.product_url}</code>{" "}
                <Link className="font-semibold text-primary hover:underline" href={`/admin/agents?run=${trace.run.id}`}>
                  voir tous les appels de ce run
                </Link>
              </li>
            )}
            {trace.organization && <li>Organisation : <strong>{trace.organization.name}</strong></li>}
            {trace.user_id && <li>Utilisateur : <code className="font-mono">{trace.user_id}</code></li>}
          </ul>
        </AdminPanel>
      )}

      {trace.error && (
        <Alert className="mb-4.5" variant="destructive">
          <AlertTitle>Erreur</AlertTitle>
          <AlertDescription>{trace.error}</AlertDescription>
        </Alert>
      )}

      <AdminPanel
        className="mb-4.5"
        title={`Recherches lancées (${queries.length}) et pages consultées (${pages.length})`}
      >
        {queries.length === 0 && pages.length === 0 ? (
          <p className="m-0 text-[12.5px] text-muted-foreground">Aucune recherche web relevée pour cet appel.</p>
        ) : (
          <div className="grid gap-4">
            {queries.length > 0 && (
              <ul className="m-0 grid list-none gap-2 p-0 text-[12.5px]">
                {queries.map((step, index) => (
                  <li className="flex items-center gap-2" key={index}>
                    <Search className="size-3.5 shrink-0 text-muted-foreground" /> {step.query}
                    {step.status && step.status !== "completed" && (
                      <small className="text-muted-foreground">({step.status})</small>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {pages.length > 0 && (
              <ul className="m-0 grid list-none gap-2 p-0 text-[12.5px]">
                {pages.map((step, index) => (
                  <li key={index}>
                    <a
                      className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline"
                      href={step.url}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {step.title || step.url} <ExternalLink className="size-3" />
                    </a>
                    {step.date && <small className="ml-2 text-muted-foreground">{step.date}</small>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </AdminPanel>

      <AdminPanel className="mb-4.5" title="Prompts envoyés">
        <div className="grid gap-4">
          {prompts.length === 0 ? (
            <p className="m-0 text-[12.5px] text-muted-foreground">Prompt non disponible pour cette trace.</p>
          ) : (
            prompts.map((prompt, index) => (
              <div className="grid gap-2" key={index}>
                <span className="text-[12px] font-medium text-muted-foreground">
                  {prompt.label}
                </span>
                <pre className={preClass}>{prompt.text}</pre>
              </div>
            ))
          )}
          <details className="rounded-lg border p-3 text-[12px]">
            <summary className="cursor-pointer font-semibold">Requête complète (JSON brut)</summary>
            <pre className={`${preClass} mt-3`}>{prettyJson(trace.request)}</pre>
          </details>
        </div>
      </AdminPanel>

      <AdminPanel title="Réponse brute de l’API">
        <pre className={`${preClass} max-h-[640px]`}>{prettyJson(trace.response)}</pre>
      </AdminPanel>
    </>
  );
}
